import { useMemo } from "react";
import { Plus } from "lucide-react";
import { EmptyState, IconButton, CountBadge } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useTasks } from "../../app/hooks/useTasks.js";
import { useTaskPeople } from "./taskPeople.js";
import { TaskListRow } from "./TaskListRow.js";
import { taskRowsClassName, taskSubLine, sortTasksForList } from "./taskRow.js";
import { TasksLoading, TasksError } from "./TasksStates.js";
import styles from "./TasksCard.module.scss";

/**
 * The dashboard rail's TO-DO card — TASK | DUE, two columns (operator ruling D1, 2026-08-23). The task's name
 * over the release it belongs to, then the assigner → assignee faces and the due date. Status is carried by
 * the OVERDUE treatment alone, not a pill: this is the artist's rail card, and the four-column spreadsheet
 * (TASK · DUE · PROGRESS · ASSIGNER · ASSIGNEE · NOTIF) it used to wear belongs to the cross-roster To-Dos
 * widget it was borrowed from — at rail width those fixed columns were what forced every title to ellipsize.
 *
 * The ROW itself is `TaskListRow`, shared with the releases hub's Release prep panel: this file owns the
 * card — its head, its four states and how far the list runs before it scrolls.
 *
 * Lists what is still OPEN (`?openOnly`) — a to-do list that keeps showing finished work is not one. Clicking
 * a row opens the routed drawer (`?task=:id`); the head's accent ＋ opens the create modal (`?task=new`) — the
 * dashboard card head standard (title | count tag + accent ＋), shared with the Opportunities/Upcoming/Merch
 * boxes, so the create affordance lives ONLY there and the empty state points at it rather than carrying a
 * second CTA. Designed empty / loading / error states (4). Money-free by construction. Every band member
 * (band:read) can add + advance a task — the finer approval/delete rules are server-enforced (in the drawer).
 */
export function TasksCard({
  onOpenTask,
  onNewTask,
}: {
  onOpenTask: (taskId: string) => void;
  onNewTask: () => void;
}) {
  const canRead = useCapability("band:read");
  // OPEN only. A to-do list that keeps showing what you finished stops being a to-do list — and the count tag
  // beside the title would be counting closed work. `ix_tasks_widget` is the partial index for exactly this.
  const list = useTasks({ openOnly: true });
  const rows = useMemo(() => sortTasksForList(list.data?.items ?? []), [list.data]);
  // Resolved ONCE for the whole card rather than per row — same cached seat list either way, but the rows
  // then cannot disagree with each other about who someone is. `list.data.people` is the payload's own
  // directory, which is the only thing that can name an ORG assignee (ART-118).
  const people = useTaskPeople(list.data?.people ?? undefined);

  // band:read is the baseline for every band seat; a seat without it never reaches the dashboard. Guard anyway
  // so the widget never mounts a query it can't run.
  if (!canRead) return null;

  return (
    <section className={styles.box} aria-labelledby="tasks-heading">
      <header className={styles.head}>
        <h3 id="tasks-heading" className={styles.title}>
          To-do
        </h3>
        <div className={styles.headMeta}>
          {!list.isLoading && !list.isError && <CountBadge count={rows.length} />}
          <IconButton icon={Plus} label="New task" tone="accent" shape="circle" size="sm" onClick={onNewTask} />
        </div>
      </header>

      {list.isLoading ? (
        <TasksLoading />
      ) : list.isError ? (
        <TasksError error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="You're all caught up"
          description="No open tasks — tap ＋ to add one."
        />
      ) : (
        <div className={styles.table}>
          {/* No column heads (Ryan, ART-199 — the To-do reference is a bare list). Capped and scrollable
              (the shipped `.todo-scroll`), so a band with thirty tasks doesn't stretch the card. */}
          <ul className={taskRowsClassName(styles.rowsScroll)}>
            {rows.map((task) => (
              <TaskListRow
                key={task.taskId}
                task={task}
                people={people}
                sub={taskSubLine(task)}
                onOpen={() => task.taskId && onOpenTask(task.taskId)}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
